import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Footer from "../components/feature/Footer";

const deathMessages = [
  "You tried to load a page that doesn't exist.",
  "You fell into the void.",
  "You were slain by a broken URL.",
  "You hit the ground too hard.",
  "You starved to death — of valid routes.",
  "You were blown up by a missing page.",
  "You drowned in a sea of 404s.",
  "Killed by [Page Not Found]",
];

export default function NotFound() {
  const location = useLocation();
  const [message, setMessage] = useState("");
  const [showRespawn, setShowRespawn] = useState(false);
  const [respawning, setRespawning] = useState(false);

  useEffect(() => {
    const random = deathMessages[Math.floor(Math.random() * deathMessages.length)];
    setMessage(random);
    const timer = setTimeout(() => setShowRespawn(true), 800);
    return () => clearTimeout(timer);
  }, []);

  const handleRespawn = () => {
    setRespawning(true);
    setTimeout(() => {
      window.location.href = "/";
    }, 600);
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <main className="flex-1 flex flex-col items-center justify-center text-center px-4 py-16">

        {/* Minecraft death screen card */}
        <div className="w-full max-w-lg">

          {/* Server icon */}
          <div className="flex justify-center mb-6">
            <div
              className="relative"
              style={{ imageRendering: "pixelated" }}
            >
              <img
                src="https://storage.readdy-site.link/project_files/520edd13-5f79-4106-acab-e65aa072f983/af73a29b-2293-4869-82a9-14c04321e157_server-icon.png?v=a9cbf51f53771ede9685801f64496bc2"
                alt="AppleSMP"
                width={80}
                height={80}
                className="opacity-40 grayscale"
                style={{ imageRendering: "pixelated" }}
              />
              {/* Red X overlay */}
              <div className="absolute inset-0 flex items-center justify-center">
                <i className="ri-close-large-fill text-primary text-4xl opacity-90" />
              </div>
            </div>
          </div>

          {/* Death title */}
          <div className="mb-2">
            <h1
              className="text-4xl font-black tracking-wide text-primary mb-1"
              style={{ fontFamily: "'JetBrains Mono', monospace", textShadow: "3px 3px 0px rgba(0,0,0,0.8)" }}
            >
              You Died!
            </h1>
            <p className="text-muted-foreground text-xs uppercase tracking-widest font-mono">
              — Page Not Found —
            </p>
          </div>

          {/* Death message box */}
          <div
            className="mx-auto mt-5 rounded border border-primary/30 bg-primary/5 px-6 py-4 max-w-sm"
          >
            <p
              className="text-sm text-foreground/80 font-mono leading-relaxed"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              {message || "…"}
            </p>
            <p className="mt-2 text-xs text-muted-foreground font-mono">
              <span className="text-primary">Error:</span> {location.pathname}
            </p>
          </div>

          {/* Stats row */}
          <div className="mt-6 flex items-center justify-center gap-6 text-xs font-mono text-muted-foreground">
            <div className="flex flex-col items-center gap-1">
              <i className="ri-heart-line text-primary text-lg" />
              <span>0 / 20 HP</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <i className="ri-sword-line text-muted-foreground/50 text-lg" />
              <span>Score: 404</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <i className="ri-map-pin-line text-muted-foreground/50 text-lg" />
              <span>applesmp.us</span>
            </div>
          </div>

          {/* Buttons */}
          <div
            className={`mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 transition-all duration-500 ${
              showRespawn ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            <button
              onClick={handleRespawn}
              disabled={respawning}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 px-6 py-2.5 text-sm font-semibold font-mono transition-all cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed w-full sm:w-auto"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              {respawning ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  Respawning...
                </>
              ) : (
                <>
                  <i className="ri-refresh-line" />
                  Respawn
                </>
              )}
            </button>
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-card hover:bg-accent px-6 py-2.5 text-sm font-semibold font-mono transition-all cursor-pointer whitespace-nowrap w-full sm:w-auto"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              <i className="ri-home-line" />
              Title Screen
            </Link>
          </div>

          {/* Quick links */}
          <div className="mt-8 pt-6 border-t border-border/30">
            <p className="text-xs text-muted-foreground mb-3 font-mono">Quick links:</p>
            <div className="flex flex-wrap justify-center gap-2">
              {[
                { label: "Leaderboard", to: "/leaderboard", icon: "ri-trophy-line" },
                { label: "Market", to: "/market", icon: "ri-bar-chart-line" },
                { label: "Rules", to: "/rules", icon: "ri-file-list-3-line" },
                { label: "Map", to: "/map", icon: "ri-map-line" },
              ].map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card/60 hover:bg-accent px-3 py-1.5 text-xs font-mono text-muted-foreground hover:text-foreground transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className={link.icon} />
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
